import {
  addPartesEnvolvidasSheet,
  addResponsaveisAquisicaoSheet,
  addTramitacoesSeiSheet,
  fetchGenericEntityData,
} from './auxiliar_componentes';
import { EnumOption, OrgInterRelatedData } from '../../../modules/types/types';
import { valoresExibidosCotacao } from '../../../pages/daf/ProcessosAquisitivos/orgInters/dadosGerais/components/cotacao/auxiliarCotacao';
import { valoresExibidosFiscalContrato } from '../../../pages/daf/ProcessosAquisitivos/orgInters/dadosGerais/components/ordemCompra/auxiliarOrdemCompra';
import { valoresExibidosPlanoTrabalhoOrgInter } from '../../../pages/daf/ProcessosAquisitivos/orgInters/dadosGerais/components/planoTrabalho/auxiliarPlanoTrabalho';
import { valoresExibidosTermoReferenciaOrgInter } from '../../../pages/daf/ProcessosAquisitivos/orgInters/dadosGerais/components/termoReferencia/auxiliarTermoReferencia';
import { valoresExibidosTransferenciasOrgInter } from '../../../pages/daf/ProcessosAquisitivos/orgInters/dadosGerais/components/transferenciasFinanceiras/auxiliarTransferenciasFinanceiras';
import { valoresExibidosOffCanvasOrgInters } from '../../../pages/daf/ProcessosAquisitivos/orgInters/utils/auxiliarOrgInters';
import { getAllOrgInterCotacao } from '../../../services/orgInters/orgInterCotacaoService';
import { getAllOrgInterOrdemCompra } from '../../../services/orgInters/orgInterOrdemCompraService';
import { getAllOrgInterPlanoTrabalho } from '../../../services/orgInters/orgInterPlanoTrabalhoService';
import { getAllOrgInterTermoReferencia } from '../../../services/orgInters/orgInterTermoReferenciaService';
import { getAllOrgInterTransfFin } from '../../../services/orgInters/orgInterTransfFinService';
import { ExportSheet } from '../core';
import { baseLogColumns, DataPreparer } from '../factory';

export class OrgInterExportPreparer implements DataPreparer {
  private modulo = 'orgInter';

  /**
   * Busca todos os relacionados de OrgInter
   */
  private async fetchRelatedData(
    orgInterIds: number[],
  ): Promise<OrgInterRelatedData> {
    if (orgInterIds.length === 0) return {};

    try {
      const [allPlanos, allTransferencias, allTermos, allCotacoes, allOrdens] =
        await Promise.all([
          this.fetchOrgInterPlanos(),
          this.fetchOrgInterTransferencias(),
          this.fetchOrgInterTermos(),
          this.fetchOrgInterCotacoes(),
          this.fetchOrgInterOrdensCompra(),
        ]);

      const planosTrabalho = this.filterByOrgInterIds(allPlanos, orgInterIds);
      const transfFinanceiras = this.filterByOrgInterIds(
        allTransferencias,
        orgInterIds,
      );
      const termosReferencia = this.filterByOrgInterIds(allTermos, orgInterIds);
      const cotacoes = this.filterByOrgInterIds(allCotacoes, orgInterIds);
      const ordensCompra = this.filterByOrgInterIds(allOrdens, orgInterIds);

      const [partesEnvolvidas, responsaveisAquisicao, tramitacoes] =
        await Promise.all([
          fetchGenericEntityData('parteenvolvida', this.modulo, orgInterIds),
          fetchGenericEntityData(
            'responsavelaquisicao',
            this.modulo,
            orgInterIds,
          ),
          fetchGenericEntityData('tramitacoes', this.modulo, orgInterIds),
        ]);

      return {
        planosTrabalho,
        transfFinanceiras,
        termosReferencia,
        cotacoes,
        ordensCompra,
        partesEnvolvidas,
        responsaveisAquisicao,
        tramitacoes,
      };
    } catch (error) {
      console.error('Erro ao buscar dados relacionados do OrgInter:', error);
      return {};
    }
  }

  private async fetchOrgInterPlanos(): Promise<any[]> {
    try {
      const result = await getAllOrgInterPlanoTrabalho();
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error(
        'Erro ao buscar Planos de Trabalho do Organismo Internacional:',
        error,
      );
      return [];
    }
  }

  private async fetchOrgInterTransferencias(): Promise<any[]> {
    try {
      const result = await getAllOrgInterTransfFin();
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error(
        'Erro ao buscar Transferências Financeiras do Organismo Internacional:',
        error,
      );
      return [];
    }
  }

  private async fetchOrgInterTermos(): Promise<any[]> {
    try {
      const result = await getAllOrgInterTermoReferencia();
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error(
        'Erro ao buscar Termos de Referência do Organismo Internacional:',
        error,
      );
      return [];
    }
  }

  private async fetchOrgInterCotacoes(): Promise<any[]> {
    try {
      const result = await getAllOrgInterCotacao();
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error(
        'Erro ao buscar Cotações do Organismo Internacional:',
        error,
      );
      return [];
    }
  }

  private async fetchOrgInterOrdensCompra(): Promise<any[]> {
    try {
      const result = await getAllOrgInterOrdemCompra();
      return Array.isArray(result) ? result : [];
    } catch (error) {
      console.error(
        'Erro ao buscar Ordens de Compra do Organismo Internacional:',
        error,
      );
      return [];
    }
  }

  private filterByOrgInterIds(data: any[], orgInterIds: number[]): any[] {
    if (!Array.isArray(data) || data.length === 0) return [];

    return data.filter((item) => {
      // Acessa o id do OrgInter através das seguintes formas possíveis
      const orgInterId = item.orgInter?.id || item.orgInterId;
      return orgInterIds.includes(orgInterId);
    });
  }

  async prepareForExport(
    selectedOrgInters: any[],
    additionalData?: {
      statusOrgInter?: EnumOption[];
      componenteAF?: EnumOption[];
      statusTransFin?: EnumOption[];
      statusOrdemCompra?: EnumOption[];
    },
  ): Promise<ExportSheet[]> {
    const sheets: ExportSheet[] = [];
    const orgInterIds = selectedOrgInters.map((o) => o.id);

    const {
      statusOrgInter = [],
      componenteAF = [],
      statusTransFin = [],
      statusOrdemCompra = [],
    } = additionalData || {};

    // Sheet principal - Organismos Internacionais
    sheets.push({
      name: 'Organismos Internacionais',
      data: selectedOrgInters.map((orgInter) =>
        this.transformOrgInter(orgInter, statusOrgInter, componenteAF),
      ),
      columns: {
        ...baseLogColumns,
        ...valoresExibidosOffCanvasOrgInters,
      },
    });

    // 2. Buscar dados relacionados
    const relatedData = await this.fetchRelatedData(orgInterIds);

    // 3. Adicionar sheets relacionados
    this.addPlanosTrabalhoSheet(sheets, relatedData.planosTrabalho);
    this.addTransfFinanceiraSheet(
      sheets,
      statusTransFin,
      relatedData.transfFinanceiras,
    );
    this.addTermosReferenciaSheet(
      sheets,
      statusOrgInter,
      relatedData.termosReferencia,
    );
    this.addCotacoesSheet(sheets, relatedData.cotacoes);
    this.addOrdensCompraSheet(
      sheets,
      statusOrdemCompra,
      relatedData.ordensCompra,
    );
    addPartesEnvolvidasSheet(sheets, this.modulo, relatedData.partesEnvolvidas);
    addResponsaveisAquisicaoSheet(
      sheets,
      this.modulo,
      relatedData.responsaveisAquisicao,
    );
    addTramitacoesSeiSheet(sheets, this.modulo, relatedData.tramitacoes);

    return sheets;
  }

  /**
   * Transformação principal do Organismo Internacional
   */
  private transformOrgInter = (
    orgInter: any,
    statusOrgInter: EnumOption[],
    componenteAF: EnumOption[],
  ) => {
    return {
      // logs
      id: orgInter.id,
      usuarioRegistro: orgInter.usuarioRegistro?.nomeCompleto || 'N/A',
      dataRegistro: orgInter.dataRegistro,
      dataUltimaAtualizacao: orgInter.dataUltimaAtualizacao,
      usuarioAtualizacao: orgInter.usuarioAtualizacao?.nomeCompleto || 'N/A',
      usuarioUltimaVisualizacao:
        orgInter.usuarioUltimaVisualizacao?.nomeCompleto || 'N/A',
      nEdicoes: orgInter.nEdicoes || 0,
      nVisualizacoes: orgInter.nVisualizacoes || 0,
      nVisualizadores: orgInter.nVisualizadores || 0,

      // módulo
      statusOrgInter:
        statusOrgInter[orgInter.statusOrgInter]?.label ||
        orgInter.statusOrgInter,
      componenteAf:
        componenteAF[orgInter.componenteAf]?.label || orgInter.componenteAf,
      orgNome: orgInter.orgNome || 'N/A',
      orgSigla: orgInter.orgSigla || 'N/A',

      numeroTC: orgInter.numeroTC || 'N/A',
      numeroTermoAjuste: orgInter.numeroTermoAjuste || 'N/A',
      nupTermoAjuste: orgInter.nupTermoAjuste || 'N/A',

      dataInicio: orgInter.dataInicio,
      dataVigencia: orgInter.dataVigencia,

      observacoes: orgInter.observacoes || '',
    };
  };

  /**
   * Adiciona sheet de Planos de Trabalho se houver dados
   */
  private addPlanosTrabalhoSheet(sheets: ExportSheet[], planos?: any[]) {
    if (!planos?.length) return;

    sheets.push({
      name: 'Planos de Trabalho',
      data: planos.map(this.transformPlanoTrabalho),
      columns: {
        ...baseLogColumns,
        ...valoresExibidosPlanoTrabalhoOrgInter,
        orgInterId: 'ID ORG. INTER',
      },
    });
  }

  private transformPlanoTrabalho = (plano: any) => {
    return {
      //logs
      id: plano.id,
      usuarioRegistro: plano.usuarioRegistro?.nomeCompleto || 'N/A',
      dataRegistro: plano.dataRegistro,
      dataUltimaAtualizacao: plano.dataUltimaAtualizacao,
      usuarioAtualizacao: plano.usuarioAtualizacao?.nomeCompleto || 'N/A',
      nEdicoes: plano.nEdicoes || 0,
      nVisualizacoes: plano.nVisualizacoes || 0,
      nVisualizadores: plano.nVisualizadores || 0,

      //Dados de Relacionamentos
      orgInterId: plano.orgInter.id || 'N/A',
      produtoDAF: plano.produtoDAF.produtoFarmaceutico || 'N/A',

      //Dados do Plano de Trabalho
      valorUnitario: plano.valorUnitario || 'N/A',
      taxaAdm: plano.taxaAdm || 'N/A',
      valorFrete: plano.valorFrete || 'N/A',
      observacoes: plano.observacoes || '',
    };
  };

  /**
   * Adiciona sheet de Transferências Financeiras se houver dados
   */
  private addTransfFinanceiraSheet(
    sheets: ExportSheet[],
    statusTransFin: EnumOption[],
    transferencias?: any[],
  ) {
    if (!transferencias?.length) return;

    sheets.push({
      name: 'Transferências Financeiras',
      data: transferencias.map((transferencia) =>
        this.transformTransfFinanceira(transferencia, statusTransFin),
      ),
      columns: {
        ...baseLogColumns,
        ...valoresExibidosTransferenciasOrgInter,
        orgInterId: 'ID ORG. INTER',
      },
    });
  }

  private transformTransfFinanceira = (
    transferencia: any,
    statusTransFin: EnumOption[],
  ) => {
    return {
      //logs
      id: transferencia.id,
      usuarioRegistro: transferencia.usuarioRegistro?.nomeCompleto || 'N/A',
      dataRegistro: transferencia.dataRegistro,
      dataUltimaAtualizacao: transferencia.dataUltimaAtualizacao,
      usuarioAtualizacao:
        transferencia.usuarioAtualizacao?.nomeCompleto || 'N/A',
      nEdicoes: transferencia.nEdicoes || 0,
      nVisualizacoes: transferencia.nVisualizacoes || 0,
      nVisualizadores: transferencia.nVisualizadores || 0,

      //Dados de Relacionamentos
      orgInterId: transferencia.orgInter.id || 'N/A',

      //Dados do Transferencia de Trabalho
      status: statusTransFin[transferencia.statusTransferencia]?.label || 'N/A',
      contratoCambio: transferencia.contratoCambio || 'N/A',
      dataContrato: transferencia.dataContrato || 'N/A',
      valorTransferido: transferencia.valorTransferido || 'N/A',
      cotacaoDolar: transferencia.cotacaoDolar || 'N/A',
      valorTotalDolares: transferencia.valorTotalDolar || 'N/A',
      observacoes: transferencia.observacoes || '',
    };
  };

  /**
   * Adiciona sheet de Termos de Referência se houver dados
   */
  private addTermosReferenciaSheet(
    sheets: ExportSheet[],
    statusOrgInter: EnumOption[],
    termos?: any[],
  ) {
    if (!termos?.length) return;

    sheets.push({
      name: 'Termos de Referência',
      data: termos.map((termo) =>
        this.transformTermoReferencia(termo, statusOrgInter),
      ),
      columns: {
        ...baseLogColumns,
        ...valoresExibidosTermoReferenciaOrgInter,
        orgInterId: 'ID ORG. INTER',
      },
    });
  }

  private transformTermoReferencia = (
    termo: any,
    statusOrgInter: EnumOption[],
  ) => {
    return {
      //logs
      id: termo.id,
      usuarioRegistro: termo.usuarioRegistro?.nomeCompleto || 'N/A',
      dataRegistro: termo.dataRegistro,
      dataUltimaAtualizacao: termo.dataUltimaAtualizacao,
      usuarioAtualizacao: termo.usuarioAtualizacao?.nomeCompleto || 'N/A',
      nEdicoes: termo.nEdicoes || 0,
      nVisualizacoes: termo.nVisualizacoes || 0,
      nVisualizadores: termo.nVisualizadores || 0,

      //Dados de Relacionamentos
      orgInterId: termo.orgInter.id || 'N/A',

      //Dados do Termo de Trabalho
      status: statusOrgInter[termo.statusTermoRef]?.label || 'N/A',
      termoReferencia: termo.termoReferencia || 'Não informado',
      dataTermoRef: termo.dataTermoRef || 'N/A',
      observacoes: termo.observacoes || '',
    };
  };

  /**
   * Adiciona sheet de Cotações de Produtos se houver dados
   */
  private addCotacoesSheet(sheets: ExportSheet[], cotacoes?: any[]) {
    if (!cotacoes?.length) return;

    sheets.push({
      name: 'Cotações de Produtos',
      data: cotacoes.map(this.transformCotacao),
      columns: {
        ...baseLogColumns,
        ...valoresExibidosCotacao,
        orgInterId: 'ID ORG. INTER',
      },
    });
  }

  private transformCotacao = (cotacao: any) => {
    return {
      //logs
      id: cotacao.id,
      usuarioRegistro: cotacao.usuarioRegistro?.nomeCompleto || 'N/A',
      dataRegistro: cotacao.dataRegistro,
      dataUltimaAtualizacao: cotacao.dataUltimaAtualizacao,
      usuarioAtualizacao: cotacao.usuarioAtualizacao?.nomeCompleto || 'N/A',
      nEdicoes: cotacao.nEdicoes || 0,
      nVisualizacoes: cotacao.nVisualizacoes || 0,
      nVisualizadores: cotacao.nVisualizadores || 0,

      //Dados de Relacionamentos
      orgInterId: cotacao.orgInter.id || 'N/A',
      produtoDAF: cotacao.produtoDAF.produtoFarmaceutico || 'N/A',

      //Dados do Cotacao de Trabalho
      valorUnitario: cotacao.valorUnitario,
      cotacaoDolar: cotacao.cotacaoDolar,
      dataCotacao: cotacao.dataCotacao,
      qtdCotada: cotacao.qtdCotada,
      valorTotal: cotacao.valorTotal,
      observacoes: cotacao.observacoes || '',
    };
  };

  /**
   * Adiciona sheet de Ordens de Compra se houver dados
   */
  private addOrdensCompraSheet(
    sheets: ExportSheet[],
    statusOrdemCompra: EnumOption[],
    ordensCompra?: any[],
  ) {
    if (!ordensCompra?.length) return;

    sheets.push({
      name: 'Ordens de Compra',
      data: ordensCompra.map((ordem) =>
        this.transformOrdemCompra(ordem, statusOrdemCompra),
      ),
      columns: {
        ...baseLogColumns,
        ...valoresExibidosFiscalContrato,
        orgInterId: 'ID ORG. INTER',
      },
    });
  }

  private transformOrdemCompra = (
    ordemCompra: any,
    statusOrdemCompra: EnumOption[],
  ) => {
    return {
      //logs
      id: ordemCompra.id,
      usuarioRegistro: ordemCompra.usuarioRegistro?.nomeCompleto || 'N/A',
      dataRegistro: ordemCompra.dataRegistro,
      dataUltimaAtualizacao: ordemCompra.dataUltimaAtualizacao,
      usuarioAtualizacao: ordemCompra.usuarioAtualizacao?.nomeCompleto || 'N/A',
      nEdicoes: ordemCompra.nEdicoes || 0,
      nVisualizacoes: ordemCompra.nVisualizacoes || 0,
      nVisualizadores: ordemCompra.nVisualizadores || 0,

      //Dados de Relacionamentos
      orgInterId: ordemCompra.orgInter.id || 'N/A',
      produtoDAF: ordemCompra.produtoDAF.produtoFarmaceutico || 'N/A',

      //Dados do Ordem de Compra
      status: statusOrdemCompra[ordemCompra.statusOrdem]?.label || 'N/A',
      numeracaoOrdem: ordemCompra.numeracaoOrdem || 'N/A',
      embalagemFator: ordemCompra.embalagemFator || 'N/A',
      qtdEncomendada: ordemCompra.qtdEncomendada || 'N/A',
      dataOrdem: ordemCompra.dataOrdem || 'N/A',
      dataPrevisao: ordemCompra.dataPrevisao || 'N/A',

      observacoes: ordemCompra.observacoes || '',
    };
  };

  //statusTransFin - Transf financeiras   const statusTransFin = useStatusTransfFin();
  //statusOrgInter - Termo Referencia   const statusOrgInter = useStatusOrgInter();
  // statusOrdemCompra - Ordem Compra   const statusOrdemOptions = useStatusOrdemCompra();
}
